import { m } from 'framer-motion';
import { SectionHeading } from './SectionHeading';
import { ArrowUpRight } from './Ornaments';

const projects = [
  {
    name: "i3dify",
    description: "Showcase your designs as real 3D experiences",
    url: "https://i3dify.com/",
    logo: "/logos/i3dify.jpg",
  },
  {
    name: "World of Gami",
    description: "An AI-powered Trello alternative that ensures your team keeps tasks up-to-date",
    url: "https://www.worldofgami.com/",
    logo: "/logos/wog.jpg",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 }
  },
};

export function Projects({ isDark = true }) {
  // No bottom margin here on purpose: the footer's top padding owns the
  // closing break, so that gap is declared in one place instead of being
  // the sum of two values in two files.
  return (
    <m.section
      id="projects"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <SectionHeading index="03" title="projects" isDark={isDark} />

      <m.p
        className={`u-prose text-[1.0625rem] leading-[1.75] transition-colors ${
          isDark ? 'text-neutral-400' : 'text-neutral-600'
        }`}
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.15 }}
        viewport={{ once: true }}
      >
        In his free time, you'll find him with a{' '}
        <a
          href="https://en.wikipedia.org/wiki/Mate_(drink)"
          target="_blank"
          rel="noopener noreferrer"
          className={`underline decoration-1 underline-offset-[0.2em] transition-colors ${
            isDark
              ? 'text-neutral-200 decoration-neutral-700 hover:decoration-neutral-300'
              : 'text-neutral-900 decoration-neutral-300 hover:decoration-neutral-700'
          }`}
        >
          mate
        </a>{' '}
        in hand, building side projects to experiment with emerging technologies
        and explore new possibilities.
      </m.p>

      <m.div
        className="-mx-3 mt-8 space-y-1"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
      >
        {projects.map((project) => (
          <m.a
            key={project.name}
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            variants={itemVariants}
            className={`group flex items-center gap-4 rounded-2xl px-3 py-3.5 transition-colors duration-300 ${
              isDark ? 'hover:bg-white/[0.028]' : 'hover:bg-black/[0.028]'
            }`}
          >
            <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-[11px]">
              {project.logo ? (
                <img
                  src={project.logo}
                  alt={`${project.name} logo`}
                  width={40}
                  height={40}
                  loading="lazy"
                  className={`h-full w-full object-cover transition-all duration-500 ${
                    isDark
                      ? 'opacity-[0.72] saturate-[0.85] group-hover:opacity-100 group-hover:saturate-100'
                      : 'opacity-80 saturate-[0.9] group-hover:opacity-100 group-hover:saturate-100'
                  }`}
                />
              ) : (
                <span
                  className={`flex h-full w-full items-center justify-center font-display text-lg ${
                    isDark ? 'bg-white/5 text-neutral-400' : 'bg-black/5 text-neutral-600'
                  }`}
                >
                  {project.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3
                  className={`text-[0.9375rem] font-medium leading-snug tracking-[-0.006em] transition-colors ${
                    isDark
                      ? 'text-neutral-300 group-hover:text-neutral-50'
                      : 'text-neutral-800 group-hover:text-black'
                  }`}
                >
                  {project.name}
                </h3>
                {/* Arrow travels along its own diagonal on hover */}
                <span
                  className={`ml-auto flex-shrink-0 transition-all duration-300 group-hover:-translate-y-px group-hover:translate-x-px ${
                    isDark
                      ? 'text-neutral-700 group-hover:text-neutral-400'
                      : 'text-neutral-300 group-hover:text-neutral-600'
                  }`}
                >
                  <ArrowUpRight className="h-3 w-3" />
                </span>
              </div>
              <p
                className={`mt-0.5 text-[0.8125rem] leading-snug transition-colors ${
                  isDark
                    ? 'text-neutral-500 group-hover:text-neutral-400'
                    : 'text-neutral-500 group-hover:text-neutral-600'
                }`}
              >
                {project.description}
              </p>
            </div>
          </m.a>
        ))}
      </m.div>
    </m.section>
  );
}
